import { AlertTriangle, ClipboardList, Download, Users } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { ExistingContactMode } from '../wizardState'

interface ModeOption {
  value: ExistingContactMode
  label: string
  description: string
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'keep',
    label: 'Keep existing',
    description:
      'Skip these rows — they won’t be imported and existing contacts are left unchanged.',
  },
  {
    value: 'update',
    label: 'Update with imported values',
    description:
      "Import these rows and update the existing contacts. Blank cells won't overwrite existing data.",
  },
]

interface ModeRadioGroupProps {
  selected: ExistingContactMode
  onSelect: (value: ExistingContactMode) => void
}

/**
 * Accessible radio group for the CRM-duplicate handling mode. Each option shows
 * a bold label plus a one-line description; the whole row is a clickable label.
 */
function ModeRadioGroup({ selected, onSelect }: ModeRadioGroupProps) {
  return (
    <div className="flex flex-col gap-2">
      {MODE_OPTIONS.map((opt) => {
        const checked = selected === opt.value
        return (
          <label
            key={opt.value}
            className={cn(
              'flex cursor-pointer select-none items-start gap-2 rounded-lg border px-3 py-2 transition-colors',
              checked
                ? 'border-brand bg-brand/5'
                : 'border-line hover:bg-surface-hover'
            )}
          >
            <input
              type="radio"
              name="existing-contact-mode"
              checked={checked}
              onChange={() => onSelect(opt.value)}
              className="mt-0.5 size-3.5 cursor-pointer accent-brand"
            />
            <span className="flex flex-col gap-0.5">
              <span
                className={cn(
                  'text-xs font-medium transition-colors',
                  checked ? 'text-fg' : 'text-fg-secondary'
                )}
              >
                {opt.label}
              </span>
              <span className="text-xs text-fg-muted">{opt.description}</span>
            </span>
          </label>
        )
      })}
    </div>
  )
}

interface DecisionBarProps {
  /** Count of rows excluded because they have validation errors. */
  errorCount: number
  /** Count of rows whose email already exists in the org's CRM. */
  crmDuplicateCount: number
  existingContactMode: ExistingContactMode
  onExistingContactModeChange: (value: ExistingContactMode) => void
  willImport: number
  total: number
  onDownloadFailed: () => void
}

/**
 * "Review" bar shown above the wizard footer when the import needs a decision.
 *
 * Two independent sections, each only rendered when relevant:
 *
 * - Errors block (errorCount > 0): errored rows are ALWAYS excluded — there is
 *   no user choice. Shows the count plus a "Download N failed rows" link so the
 *   user can fix and re-upload.
 * - CRM-duplicate mode (crmDuplicateCount > 0): contacts whose email already
 *   exists in the CRM are handled per the chosen mode — "Keep existing"
 *   (default; the rows are EXCLUDED/skipped, existing contacts untouched) or
 *   "Update with imported values" (the rows are imported and the existing
 *   contacts updated, blank cells preserved). The willImport count reflects the
 *   mode. In-file duplicates are not shown here; the backend merges them
 *   automatically and they always import.
 *
 * When neither errors nor CRM duplicates exist, the caller hides this bar.
 */
export function DecisionBar({
  errorCount,
  crmDuplicateCount,
  existingContactMode,
  onExistingContactModeChange,
  willImport,
  total,
  onDownloadFailed,
}: DecisionBarProps) {
  return (
    <div className="flex items-start gap-4 border-t border-line bg-surface px-8 pb-3 pt-4">
      <div className="shrink-0 rounded-xl bg-surface-sunken p-2.5">
        <ClipboardList className="size-5 text-fg-muted" />
      </div>
      <div className="flex flex-1 flex-col gap-3">
        <span className="text-sm font-semibold text-fg">Review</span>

        {errorCount > 0 && (
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
            <p className="text-xs text-fg-secondary">
              {errorCount} row{errorCount !== 1 ? 's' : ''} ha
              {errorCount !== 1 ? 've' : 's'} errors and will be excluded.
            </p>
          </div>
        )}

        {crmDuplicateCount > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Users className="size-3.5 shrink-0 text-fg-secondary" />
              <span className="text-xs font-semibold text-fg-secondary">
                {crmDuplicateCount} contact
                {crmDuplicateCount !== 1 ? 's' : ''} already exist
                {crmDuplicateCount !== 1 ? '' : 's'} in your CRM.
              </span>
            </div>
            <ModeRadioGroup
              selected={existingContactMode}
              onSelect={onExistingContactModeChange}
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5 self-stretch">
        {errorCount > 0 && (
          <button
            type="button"
            onClick={onDownloadFailed}
            className="flex items-center gap-1.5 text-xs text-fg-secondary transition-colors hover:text-fg"
          >
            <Download className="size-3.5" />
            Download {errorCount} failed row{errorCount !== 1 ? 's' : ''}
          </button>
        )}
        <span className="mt-auto text-xs text-fg-muted">
          {willImport} of {total} row{total !== 1 ? 's' : ''} will be imported
        </span>
      </div>
    </div>
  )
}
